import { fetchAuthSession } from 'aws-amplify/auth';

export const ANALYTICS_CLIENT_ID_STORAGE_KEY = 'cs_activity_client_id';
export const MATOMO_VISITOR_ID_STORAGE_KEY = 'cs_matomo_visitor_id';

const HEX_ALPHABET = '0123456789abcdef';
const PAGEVIEW_ID_ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

let memoryMatomoVisitorId: string | null = null;

function randomFromAlphabet(alphabet: string, length: number): string {
  const bytes =
    typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function'
      ? crypto.getRandomValues(new Uint8Array(length))
      : null;

  let value = '';
  for (let index = 0; index < length; index += 1) {
    const byte = bytes ? bytes[index] : Math.floor(Math.random() * 256);
    value += alphabet[byte % alphabet.length];
  }
  return value;
}

function isMatomoVisitorId(value: string | null): value is string {
  return Boolean(value && /^[0-9a-f]{16}$/i.test(value));
}

export function getOrCreateAnalyticsClientId(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const existing = window.localStorage.getItem(ANALYTICS_CLIENT_ID_STORAGE_KEY);
    if (existing) return existing;

    const created =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `client_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    window.localStorage.setItem(ANALYTICS_CLIENT_ID_STORAGE_KEY, created);
    return created;
  } catch {
    return null;
  }
}

export function getOrCreateMatomoVisitorId(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const existing = window.localStorage.getItem(MATOMO_VISITOR_ID_STORAGE_KEY);
    if (isMatomoVisitorId(existing)) return existing.toLowerCase();

    const created = randomFromAlphabet(HEX_ALPHABET, 16);
    window.localStorage.setItem(MATOMO_VISITOR_ID_STORAGE_KEY, created);
    return created;
  } catch {
    if (!memoryMatomoVisitorId) {
      memoryMatomoVisitorId = randomFromAlphabet(HEX_ALPHABET, 16);
    }
    return memoryMatomoVisitorId;
  }
}

export function createMatomoPageviewId(): string {
  return randomFromAlphabet(PAGEVIEW_ID_ALPHABET, 6);
}

export async function getOptionalAnalyticsAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    const accessToken = session.tokens?.accessToken?.toString();
    return idToken || accessToken || null;
  } catch {
    return null;
  }
}
